import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { isAfter, startOfDay, sub } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { uniqBy } from 'remeda';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map, pluck, take } from 'rxjs/operators';
import { AuthService, LoHiUserInfo } from '../../../root/auth.service';
import { DriversService } from '../../../root/drivers.service';
import { ActiveDriver, DriverStat, EarningsPerDay, PendingDriver, ReportImprovements } from '../../../types';

@Component({
  selector: 'lohi-scorecard',
  templateUrl: './scorecard.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScorecardComponent implements OnInit {
  public driverStats$: Observable<DriverStat>;
  public currentDriver$: Observable<ActiveDriver | PendingDriver>;
  public daysToCompleteShifts$: Observable<number>;
  public reportImprovements$$ = new BehaviorSubject<ReportImprovements>(null);
  public leaderboardsWon$: Observable<number>;
  public lastWin$: Observable<string>;
  public currentUser$: Observable<LoHiUserInfo>;

  constructor(private driverService: DriversService, private logger: NGXLogger, private initService: AuthService) {}

  public ngOnInit(): void {
    this.currentDriver$ = this.driverService.currentDriver$;
    this.driverStats$ = combineLatest([
      this.driverService.driverStats$.pipe(pluck('drivers')),
      this.currentDriver$,
    ]).pipe(
      filter(([drivers, driver]) => !!drivers && !!driver),
      map(([drivers, driver]) => {
        for (const driver1 of drivers) {
          if (driver1.UserId === driver.id) {
            return driver1;
          }
        }
        return null;
      }),
    );
    this.currentUser$ = this.initService.userInfo$;

    this.daysToCompleteShifts$ = this.driverStats$.pipe(
      pluck('driver_earnings_per_day'),
      map((value: EarningsPerDay[]) => {
        const daysAgo = sub(new Date(), { days: 10 });
        return value
          .filter((value1) => (value1.earnings || value1.lostEarnings) && isAfter(daysAgo, new Date(value1.date)))
          .map((value1) => startOfDay(new Date(value1.date)));
      }),
      map(uniqBy((value) => value.getTime())),
      pluck('length'),
    );

    this.leaderboardsWon$ = this.driverStats$.pipe(
      pluck('leaderboardCounts'),
      map((value) => value?.filter((value1) => value1.winner).length),
    );

    this.lastWin$ = this.driverStats$.pipe(
      pluck('leaderboardCounts'),
      map((value) => value?.filter((value1) => value1.winner)[0]?.completedAt),
    );

    this.setupImprovements();
  }

  private async setupImprovements() {
    const driver = await this.currentDriver$
      .pipe(
        filter((d) => !!d),
        take(1),
      )
      .toPromise();
    try {
      const reportImprovements = await this.driverService.getDriverImprovements(driver.id);
      this.reportImprovements$$.next({
        ...reportImprovements,
        improvements: reportImprovements.improvements.filter((i) => i.lostCents > 0),
      });
    } catch (error) {
      this.logger.error(error);
    }
  }
}
